import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Screen } from '../../app/Frame';
import { useMeContext, useWritable } from '../../app/MeContext';
import { useScreen, useSheet, useToast } from '../../app/shell-context';
import { ApiError } from '../../api/client';
import { formatDateTime, formatDay, formatISODay, formatMinutes, weekdayRu } from '../../lib/time';
import { formatKop, kopToRubString, rubStringToKop } from '../../lib/money';
import { plural, pluralWithCount } from '../../lib/plural';
import { ChevronIcon } from '../../ui/icons';
import { Notice, Pill } from '../../ui/parts';
import { toRows, toRules, type HourRow } from '../../staff/schedule-rows';
import {
  useAdminDirections, useAdminServices, useAdminSpecialists, useBasePayout, useRateMutations, useSaveSpecialistSchedule,
  useSpecialistSchedule, useValidateSpecialistSchedule,
} from '../api';
import type { Conflict, Rate, WorkRule } from '../../staff/types';
import type { AdminSpecialist, Direction, Service } from '../types';

/**
 * Расписание чужого специалиста. Автор расписания — сам специалист; админ может
 * поправить, но правка уходит ему диффом, а не молча.
 */
export function StaffSchedule() {
  const me = useMeContext();
  const writable = useWritable();
  const sheet = useSheet();
  const toast = useToast();
  const { id: raw } = useParams();
  const id = Number(raw);

  const specialists = useAdminSpecialists();
  const specialist = specialists.data?.find((s) => s.id === id) ?? null;
  const schedule = useSpecialistSchedule(Number.isFinite(id) ? id : null);
  const validate = useValidateSpecialistSchedule();
  const save = useSaveSpecialistSchedule();

  const [rows, setRows] = useState<HourRow[] | null>(null);
  useEffect(() => {
    if (schedule.data && !rows) setRows(toRows(schedule.data.rules));
  }, [schedule.data, rows]);

  useScreen('Расписание специалиста', null);

  const busy = validate.isPending || save.isPending;
  const exceptions = schedule.data?.exceptions ?? [];

  function submit() {
    if (!rows) return;
    const next = toRules(rows);
    validate.mutate(
      { id, rules: next },
      {
        onSuccess: ({ conflicts }) => {
          if (conflicts.length) {
            sheet.open(<ConflictsSheet conflicts={conflicts} onConfirm={() => doSave(next)} />);
            return;
          }
          doSave(next);
        },
        onError: (e) => toast(e instanceof ApiError ? e.message : 'Не получилось проверить сетку'),
      },
    );
  }

  function doSave(rules: WorkRule[]) {
    save.mutate(
      { id, rules },
      {
        onSuccess: () => {
          sheet.close();
          toast('Расписание сохранено, специалист получит уведомление');
        },
        onError: (e) => toast(e instanceof ApiError ? e.message : 'Не получилось сохранить'),
      },
    );
  }

  if (schedule.isPending || !rows) {
    return <Screen><p className="lead">Загружаем расписание…</p></Screen>;
  }

  const settings = schedule.data!.settings;

  return (
    <Screen>
      <div>
        <p className="eyebrow">{specialist?.display_name ?? 'Специалист'}</p>
        <p className="lead" style={{ marginTop: 6 }}>
          Из этой сетки строятся свободные окна для закреплённых детей.
        </p>
      </div>

      {settings.status === 'paused' ? (
        <Notice>
          Запись приостановлена
          {settings.paused_until ? ` до ${formatDay(settings.paused_until, me.center.tz)}` : ' бессрочно'}.
          Расписание править можно — клиенты окон не увидят, пока запись не возобновят.
        </Notice>
      ) : null}

      <p className="eyebrow">Рабочие часы</p>
      <div className="hours">
        {rows.map((r, i) => (
          <div className={`hrow${r.on ? '' : ' is-off'}`} key={r.weekday}>
            <span className="wd">{weekdayRu(r.weekday)}</span>
            <span className="times">
              <input
                value={r.from}
                size={5}
                aria-label="начало"
                disabled={!writable}
                onChange={(e) => setRows((p) => p!.map((x, k) => (k === i ? { ...x, from: e.target.value } : x)))}
              />
              {' — '}
              <input
                value={r.to}
                size={5}
                aria-label="конец"
                disabled={!writable}
                onChange={(e) => setRows((p) => p!.map((x, k) => (k === i ? { ...x, to: e.target.value } : x)))}
              />
            </span>
            <button
              className="switch"
              type="button"
              aria-pressed={r.on}
              aria-label={`${weekdayRu(r.weekday, 'long')} рабочий день`}
              disabled={!writable}
              onClick={() => setRows((p) => p!.map((x, k) => (k === i ? { ...x, on: !x.on } : x)))}
            />
          </div>
        ))}
      </div>

      <p className="eyebrow" style={{ marginTop: 4 }}>Правила записи</p>
      <div className="kv">
        <div>
          <span className="k">Перерыв между записями</span>
          <span className="v">{settings.buffer_min} мин</span>
        </div>
        <div>
          <span className="k">Запись открыта на</span>
          <span className="v">{settings.horizon_days} {plural(settings.horizon_days, 'день', 'дня', 'дней')}</span>
        </div>
        <div>
          <span className="k">Не позже чем за</span>
          <span className="v">{settings.min_lead_hours} {plural(settings.min_lead_hours, 'час', 'часа', 'часов')}</span>
        </div>
      </div>
      <p className="footnote" style={{ textAlign: 'left' }}>Правила меняет сам специалист в своём кабинете</p>

      <div className="sechead" style={{ marginTop: 4 }}>
        <p className="eyebrow">Отпуск и особые дни</p>
        <span className="lead" style={{ fontSize: 13 }}>
          {exceptions.length ? pluralWithCount(exceptions.length, 'особый день', 'особых дня', 'особых дней') : ''}
        </span>
      </div>
      <div className="rows">
        {exceptions.length ? (
          exceptions.map((e) => (
            <div className="rw" key={e.id}>
              <span className="rw-body">
                <b>{formatISODay(e.date)}</b>
                <span>
                  {e.start_min === null ? 'весь день' : `${formatMinutes(e.start_min, me.center.timeFormat)} – ${formatMinutes(e.end_min ?? 0, me.center.timeFormat)}`}
                  {e.comment ? ` · ${e.comment}` : ''}
                </span>
              </span>
              {e.kind === 'off' ? <Pill kind="off">закрыто</Pill> : <Pill kind="paid">открыто</Pill>}
            </div>
          ))
        ) : (
          <p className="lead">Особых дней нет — работает по основному расписанию.</p>
        )}
      </div>

      <Notice>
        {specialist?.upcoming
          ? `У специалиста ${pluralWithCount(specialist.upcoming, 'будущая запись', 'будущие записи', 'будущих записей')}. Перед сохранением проверю, не попадают ли они в закрытое время.`
          : 'Будущих записей нет — сетку можно менять свободно.'}
      </Notice>

      {writable ? (
        <button className="btn solid" disabled={busy} onClick={submit}>
          {busy ? 'Проверяем…' : 'Сохранить расписание'}
        </button>
      ) : null}
    </Screen>
  );
}

function ConflictsSheet({ conflicts, onConfirm }: { conflicts: Conflict[]; onConfirm: () => void }) {
  const me = useMeContext();
  const sheet = useSheet();
  return (
    <>
      <h3>{pluralWithCount(conflicts.length, 'запись не влезает', 'записи не влезают', 'записей не влезают')} в новую сетку</h3>
      <p>Записи никуда не денутся, но новых окон в это время не будет. Лучше перенести их заранее.</p>
      <div className="rows">
        {conflicts.slice(0, 8).map((c) => (
          <div className="rw" key={c.appointmentId}>
            <span className="rw-body">
              <b>{formatDateTime(c.startsAt, me.center.tz, me.center.timeFormat)}</b>
              <span>{c.childName} · {c.reason}</span>
            </span>
          </div>
        ))}
      </div>
      <div className="row-actions">
        <button className="btn" onClick={() => sheet.close()}>Вернуться к сетке</button>
        <button className="btn solid" onClick={onConfirm}>Сохранить всё равно</button>
      </div>
    </>
  );
}

/** Услуги и ставки конкретного специалиста. */
export function StaffServices() {
  const writable = useWritable();
  const sheet = useSheet();
  const { id: raw } = useParams();
  const id = Number(raw);

  const specialists = useAdminSpecialists();
  const specialist = specialists.data?.find((s) => s.id === id) ?? null;
  const schedule = useSpecialistSchedule(Number.isFinite(id) ? id : null);
  const services = useAdminServices();
  const directions = useAdminDirections();

  useScreen('Услуги и ставки', null);

  const rates = schedule.data?.rates ?? [];
  // В списке только исключения. Базовые условия действуют на все услуги
  // категорий молча — перечислять их значит превращать одну настройку в
  // двенадцать строк, ровно от которых базовые условия и избавляют.
  const exceptions = rates.filter((r) => r.source !== 'base');
  const own = new Set(exceptions.map((r) => r.service_id));
  const hasBase = specialist ? basePayoutSet(specialist) : false;

  // Предлагаем услуги категорий специалиста, у которых своих условий ещё нет:
  // на чужую категорию ставка не встанет, и сервер её не примет.
  const mine = new Set(specialist?.direction_ids ?? []);
  const available = (services.data ?? [])
    .filter((s) => !own.has(s.id) && s.is_active && mine.has(s.direction_id));

  // Группируем так же, как на экране услуг: порядок категорий и порядок услуг
  // внутри категории — тот, что задал администратор перетаскиванием.
  const groups = (directions.data ?? [])
    .filter((d) => mine.has(d.id))
    .map((d) => ({ direction: d, items: available.filter((s) => s.direction_id === d.id) }))
    .filter((g) => g.items.length);

  const avgPrice = rates.length ? Math.round(rates.reduce((a, r) => a + r.price, 0) / rates.length) : 0;
  const avgPayout = rates.length ? Math.round(rates.reduce((a, r) => a + r.payout, 0) / rates.length) : 0;

  return (
    <Screen>
      <div>
        <p className="eyebrow">{specialist?.display_name ?? 'Специалист'}</p>
        <p className="lead" style={{ marginTop: 6 }}>
          Что специалист ведёт, по какой цене для клиента и сколько получает сам.
        </p>
      </div>

      {/* Базовые условия сверху: они покрывают все услуги категорий, а список
          ниже — исключения из них. Без этого одинаковые условия заполнялись
          по каждой услуге отдельно. */}
      {specialist ? (
        <button
          className="kv-row"
          type="button"
          disabled={!writable}
          onClick={() => sheet.open(<BasePayoutSheet specialist={specialist} />)}
        >
          <span className="k">
            Базовые условия
            {/* Список ниже — только исключения, так что действие базовых условий
                видно единственным способом: числом услуг, которые они накрыли. */}
            <small>
              {hasBase
                ? `Действуют на ${pluralWithCount(rates.length, 'услугу', 'услуги', 'услуг')}`
                : 'За любую услугу его категорий'}
            </small>
          </span>
          <span className="v">
            {basePayoutText(specialist)}
            <span className="chev"><ChevronIcon /></span>
          </span>
        </button>
      ) : null}

      <div className="rows">
        {exceptions.map((r) => (
          <button
            className="rw"
            key={r.service_id}
            disabled={!writable}
            onClick={() => sheet.open(<RateSheet specialistId={id} rate={r} />)}
          >
            <span className="rw-body">
              <b>{r.title}</b>
              <span>
                {r.duration_min} мин · {r.is_online ? 'онлайн' : 'очно'}
                {r.is_active ? '' : ' · выключена'}
              </span>
            </span>
            <span className="rw-num">
              <b>{formatKop(r.price)}</b>
              <span className="pay">{formatKop(r.payout)} специалисту</span>
            </span>
          </button>
        ))}
        {!exceptions.length ? (
          <p className="lead">
            {hasBase
              ? 'Особых условий нет: все услуги его категорий идут по базовым.'
              : 'Условий пока нет. Задайте базовые — и специалист начнёт вести все услуги своих категорий; либо задайте условия для отдельных услуг.'}
          </p>
        ) : null}
      </div>

      {writable && available.length ? (
        <button className="add" onClick={() => sheet.open(<AssignServiceSheet specialistId={id} groups={groups} />)}>
          ＋ Задать условия для услуги
        </button>
      ) : null}

      <div className="money">
        <div className="mcell">
          <span>Выручка за запись, средняя</span>
          <b>{rates.length ? formatKop(avgPrice) : '—'}</b>
        </div>
        <div className="mcell acc">
          <span>Специалисту, средняя</span>
          <b>{rates.length ? formatKop(avgPayout) : '—'}</b>
        </div>
      </div>
    </Screen>
  );
}

/** Заданы ли базовые условия. `payout_mode` сам по себе ничего не включает. */
const basePayoutSet = (s: AdminSpecialist): boolean =>
  (s.payout_mode === 'percent' ? s.payout_percent : s.payout_kop) !== null;

/** Короткая сводка базовых условий для строки: она же говорит, заданы ли они вообще. */
function basePayoutText(s: AdminSpecialist): string {
  if (s.payout_mode === 'percent') {
    return s.payout_percent === null ? 'не заданы' : `${s.payout_percent}% от цены`;
  }
  return s.payout_kop === null ? 'не заданы' : `${formatKop(s.payout_kop)} за запись`;
}

/**
 * Базовые условия оплаты. Одна настройка вместо двенадцати одинаковых форм —
 * и она же делает специалиста доступным для записи на все услуги его категорий.
 *
 * Снять условия — очистить поле: специалист снова ведёт только то, что назначено
 * ему поимённо.
 */
function BasePayoutSheet({ specialist }: { specialist: AdminSpecialist }) {
  const sheet = useSheet();
  const toast = useToast();
  const save = useBasePayout();

  const [mode, setMode] = useState<'fixed' | 'percent'>(specialist.payout_mode);
  const [payout, setPayout] = useState(specialist.payout_kop === null ? '' : kopToRubString(specialist.payout_kop));
  const [percent, setPercent] = useState(specialist.payout_percent === null ? '' : String(specialist.payout_percent));

  const payoutKop = payout.trim() ? rubStringToKop(payout) : null;
  const percentNum = percent.trim() ? Number(percent) : null;
  const ok = mode === 'percent'
    ? percentNum === null || (Number.isInteger(percentNum) && percentNum >= 0 && percentNum <= 100)
    : payout.trim() === '' || payoutKop !== null;
  const cleared = mode === 'percent' ? percentNum === null : payoutKop === null;

  return (
    <>
      <h3>Базовые условия</h3>
      <p>
        Столько специалист получает за любую услугу своих категорий. У отдельных услуг могут
        быть свои условия — они сильнее базовых.
      </p>

      <div className="chips">
        <button className="chip" aria-pressed={mode === 'fixed'} onClick={() => setMode('fixed')}>Фиксированно</button>
        <button className="chip" aria-pressed={mode === 'percent'} onClick={() => setMode('percent')}>Процент от цены</button>
      </div>

      {mode === 'fixed' ? (
        <div className="field">
          <label htmlFor="bpKop">Специалисту за запись</label>
          <div className="suffix">
            <input id="bpKop" type="number" inputMode="numeric" value={payout} onChange={(e) => setPayout(e.target.value)} />
            <i>₽</i>
          </div>
          <span className="hint">Пусто — условия сняты: останутся только назначенные услуги.</span>
        </div>
      ) : (
        <div className="field">
          <label htmlFor="bpPct">Доля специалиста</label>
          <div className="suffix">
            <input id="bpPct" type="number" inputMode="numeric" min="0" max="100" value={percent} onChange={(e) => setPercent(e.target.value)} />
            <i>%</i>
          </div>
          <span className="hint">Пусто — условия сняты: останутся только назначенные услуги.</span>
        </div>
      )}

      <Notice kind={cleared ? 'warn' : 'calm'}>
        {cleared
          ? 'Без базовых условий специалист доступен только по услугам, назначенным ему поимённо.'
          : 'Специалист начнёт вести все активные услуги своих категорий по базовой цене услуги.'}
      </Notice>

      <div className="row-actions">
        <button className="btn" onClick={() => sheet.close()}>Отмена</button>
        <button
          className="btn solid"
          disabled={!ok || save.isPending}
          onClick={() =>
            save.mutate(
              { id: specialist.id, mode, payoutKop: mode === 'fixed' ? payoutKop : null, payoutPercent: mode === 'percent' ? percentNum : null },
              {
                onSuccess: () => {
                  toast(cleared ? 'Базовые условия сняты' : 'Базовые условия сохранены');
                  sheet.close();
                },
                onError: (e) => toast(e instanceof ApiError ? e.message : 'Не получилось сохранить'),
              },
            )
          }
        >
          Сохранить
        </button>
      </div>
    </>
  );
}

function RateSheet({ specialistId, rate }: { specialistId: number; rate: Rate }) {
  const sheet = useSheet();
  const toast = useToast();
  const { save, remove } = useRateMutations();

  const [price, setPrice] = useState(kopToRubString(rate.price));
  const [mode, setMode] = useState<'fixed' | 'percent'>(rate.payout_mode);
  const [payout, setPayout] = useState(kopToRubString(rate.payout_kop ?? 0));
  const [percent, setPercent] = useState(String(rate.payout_percent ?? 50));
  const [isActive, setIsActive] = useState(!!rate.is_active);

  const priceKop = rubStringToKop(price);
  const payoutKop = rubStringToKop(payout);
  const pct = Number(percent);
  const ok = priceKop !== null && (mode === 'percent' ? pct >= 0 && pct <= 100 : payoutKop !== null);
  const margin = priceKop === null ? 0 : priceKop - (mode === 'percent' ? Math.round((priceKop * pct) / 100 / 100) * 100 : payoutKop ?? 0);

  return (
    <>
      <h3>{rate.title}</h3>
      <p>{rate.duration_min} мин · {rate.is_online ? 'онлайн' : 'очно'}</p>

      <div className="field">
        <label htmlFor="rPrice">Цена клиенту</label>
        <div className="suffix">
          <input id="rPrice" type="number" inputMode="numeric" value={price} onChange={(e) => setPrice(e.target.value)} />
          <i>₽</i>
        </div>
        <span className="hint">Пусто — берётся базовая цена услуги.</span>
      </div>

      <div className="field">
        <label>Как считаем выплату</label>
        <div className="chips">
          <button className="chip" aria-pressed={mode === 'fixed'} onClick={() => setMode('fixed')}>Фиксированная</button>
          <button className="chip" aria-pressed={mode === 'percent'} onClick={() => setMode('percent')}>Процент</button>
        </div>
      </div>

      {mode === 'fixed' ? (
        <div className="field">
          <label htmlFor="rPayout">Специалисту за запись</label>
          <div className="suffix">
            <input id="rPayout" type="number" inputMode="numeric" value={payout} onChange={(e) => setPayout(e.target.value)} />
            <i>₽</i>
          </div>
        </div>
      ) : (
        <div className="field">
          <label htmlFor="rPercent">Процент специалисту</label>
          <div className="suffix">
            <input id="rPercent" type="number" min="0" max="100" inputMode="numeric" value={percent} onChange={(e) => setPercent(e.target.value)} />
            <i>%</i>
          </div>
        </div>
      )}

      <div className="mline">
        <span>Организации остаётся</span>
        <b>{formatKop(margin)}</b>
      </div>

      <label className="check">
        <input type="checkbox" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />
        <span>Доступна для записи</span>
      </label>

      <Notice kind="calm">
        Цена и выплата копируются в запись при её создании: повышение ставки не перепишет уже
        закрытые расчёты за прошлый месяц.
      </Notice>

      <div className="row-actions">
        <button
          className="btn ghost-danger"
          disabled={remove.isPending}
          onClick={() =>
            remove.mutate(
              { specialistId, serviceId: rate.service_id },
              {
                onSuccess: () => {
                  toast('Услуга снята со специалиста');
                  sheet.close();
                },
                onError: (e) => toast(e instanceof ApiError ? e.message : 'Не получилось'),
              },
            )
          }
        >
          Снять услугу
        </button>
        <button
          className="btn solid"
          disabled={!ok || save.isPending}
          onClick={() =>
            save.mutate(
              {
                specialistId,
                serviceId: rate.service_id,
                priceKop,
                payoutMode: mode,
                payoutKop: mode === 'fixed' ? payoutKop : null,
                payoutPercent: mode === 'percent' ? pct : null,
                isActive,
              },
              {
                onSuccess: () => {
                  toast('Ставка сохранена');
                  sheet.close();
                },
                onError: (e) => toast(e instanceof ApiError ? e.message : 'Не получилось сохранить'),
              },
            )
          }
        >
          Сохранить
        </button>
      </div>
    </>
  );
}

/**
 * Условия для отдельной услуги — исключение из базовых. Список разложен по
 * категориям: у организации их шесть, а услуг под сорок — плоским списком
 * нужную не найти, и в него попадали услуги чужих категорий, на которые
 * ставка всё равно не встанет.
 */
function AssignServiceSheet({ specialistId, groups }: {
  specialistId: number;
  groups: { direction: Direction; items: Service[] }[];
}) {
  const sheet = useSheet();
  const toast = useToast();
  const { save } = useRateMutations();
  const services = groups.flatMap((g) => g.items);
  const [serviceId, setServiceId] = useState(services[0]?.id ?? 0);
  const [payout, setPayout] = useState('');

  const service = services.find((s) => s.id === serviceId);
  const payoutKop = rubStringToKop(payout);

  return (
    <>
      <h3>Условия для услуги</h3>
      <p>
        Эти условия сильнее базовых: по этой услуге специалист будет получать столько,
        сколько указано здесь.
      </p>

      <div className="field">
        <label htmlFor="asSvc">Услуга</label>
        <select id="asSvc" value={serviceId} onChange={(e) => setServiceId(Number(e.target.value))}>
          {groups.map((g) => (
            <optgroup key={g.direction.id} label={g.direction.title}>
              {g.items.map((s) => (
                <option key={s.id} value={s.id}>{s.title} · {s.duration_min} мин · {formatKop(s.base_price_kop)}</option>
              ))}
            </optgroup>
          ))}
        </select>
      </div>

      <div className="field">
        <label htmlFor="asPayout">Специалисту за запись</label>
        <div className="suffix">
          <input id="asPayout" type="number" inputMode="numeric" value={payout} onChange={(e) => setPayout(e.target.value)} />
          <i>₽</i>
        </div>
        <span className="hint">
          Цена клиенту — базовая, {service ? formatKop(service.base_price_kop) : '—'}. Изменить можно на карточке ставки.
        </span>
      </div>

      <div className="row-actions">
        <button className="btn" onClick={() => sheet.close()}>Отмена</button>
        <button
          className="btn solid"
          disabled={payoutKop === null || save.isPending}
          onClick={() =>
            save.mutate(
              { specialistId, serviceId, payoutMode: 'fixed', payoutKop, isActive: true },
              {
                onSuccess: () => {
                  toast('Условия заданы');
                  sheet.close();
                },
                onError: (e) => toast(e instanceof ApiError ? e.message : 'Не получилось назначить'),
              },
            )
          }
        >
          Сохранить
        </button>
      </div>
    </>
  );
}
